import React, { useState, useRef, useEffect } from "react";
import styles from './Dropdown.module.scss';
import PropTypes from "prop-types";
import {ReactComponent as ChevronUp} from './ChevronUp.svg';
import {ReactComponent as ChevronRight} from './ChevronRight.svg';
import useClickOutside from "../../hooks/useClickOutside";

const Dropdown = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
  error,
  searchable,
  size = 'default',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(null); // Track which parent option is expanded
  const dropdownRef = useRef(null); // Reference to the dropdown container
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const [openUpwards, setOpenUpwards] = useState(false);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 200; // Estimated max height or measure properly if possible

      // If space below is less than dropdown height and there is more space above, open upwards
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setOpenUpwards(true);
      } else {
        setOpenUpwards(false);
      }
    }
  }, [isOpen]);

  // Function to handle item selection
  const handleItemClick = (itemValue) => {
    onChange(itemValue); // Update the selected value
    setIsOpen(false); // Close dropdown on selection
    setExpanded(null); // Reset
    setSearchText('');
  };

  // Function to handle expanding/collapsing sub-options
  const handleParentClick = (itemValue) => {
    if (expanded === itemValue) {
      setExpanded(null); // Collapse if clicked again
    } else {
      setExpanded(itemValue); // Expand the clicked parent
    }
  };

  // Recursive function to render nested options
  const renderOptions = (options, isSubOption = false) => {
    return options.map((item, index) => (
      <div key={`item-${index}`}>
        {(expanded === null || (expanded !== null && isSubOption)) && <li className={`${styles.option}`}>
          <div 
            className={styles.optionItem}
            onClick={() => {
              if (item.subOptions && item.subOptions.length > 0) {
                handleParentClick(item.value); // Toggle sub-options
              } else {
                handleItemClick(item.value); // Select option if no sub-options
              }
            }}
          >
            {item.label}
            {/* Right arrow for sub-options */}
            {item.subOptions && item.subOptions.length > 0 && (
              <span className={styles.rightArrow}><ChevronRight /></span>
            )}
          </div>
        </li>}
        {/* Render sub-options as a new dropdown on the right if expanded */}
        {item.subOptions && item.subOptions.length > 0 && expanded === item.value && (
          <>
            <li className={`${styles.option}`}>
              <div
                className={styles.optionItem}
                onClick={() => setExpanded(null)}
              >
                <span className={styles.leftArrow}><ChevronRight /></span>
              </div>
            </li>
            {renderOptions(item.subOptions, true)}
          </>
        )}
      </div>
    ));
  };

  // Function to get label for the selected value, including nested options
 useEffect(() => {
    const findLabel = (options) => {
      for (let option of options) {
        if (JSON.stringify(option.value) === JSON.stringify(value)) {
          return option.label;
        }
        if (option.subOptions && option.subOptions.length > 0) {
          const subLabel = findLabel(option.subOptions);
          if (subLabel) return `${option.label} - ${subLabel}`;
        }
      }
      return null; // Return null if no label is found
    };
    setSelectedLabel(findLabel(options));
  }, [options, value]);

  useEffect(() => {
    if (searchText) {
      setFilteredOptions(options.filter(item =>
        item.label.toLowerCase().includes(searchText.toLowerCase())
      ));
    } else {
      setFilteredOptions(options);
    }
  }, [searchText, options]);

  useClickOutside(dropdownRef, () => {
    setIsOpen(false);
    setExpanded(null);
  });

  return (
    <div ref={dropdownRef} className={styles.dropdown}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.inputWrapper}>
      <div
        className={`
          ${styles.select} 
          ${size ? styles[size] : ''}
          ${error ? styles.errorInput : ''} 
          ${isOpen ? styles.openInput : ''}
          ${disabled ? styles.disabled : ''}
        `}
        onClick={() => {
          if(!disabled) {
            setIsOpen(!isOpen)
          }
        }}
      >
        {searchable ? <input 
          type="text" 
          className={styles.searchInput} 
          value={isOpen ? searchText : selectedLabel || ""}
          onChange={({ target: { value } })=> setSearchText(value)}
          disabled={disabled}
        /> : <span className={styles.selectedLabel}>{selectedLabel}</span>}
        {!disabled && <span className={`${styles.arrow} ${isOpen ? styles.upArrow : styles.downArrow}`}><ChevronUp /></span>}
      </div>
      {isOpen && (
        <ul className={`${styles.optionsList} ${openUpwards ? styles.openUp : ''}`}>
          {renderOptions(filteredOptions)}
        </ul>
      )}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};

Dropdown.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.any,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.any.isRequired,
      subOptions: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any,
          label: PropTypes.string.isRequired,
        })
      )
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  searchable: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'small'])
}

export default Dropdown;